import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { AlertCircle, Calendar as CalendarIcon, ChevronLeft, ChevronRight, FileText, LayoutGrid, List, Loader2 } from 'lucide-react';
import { ThemeProvider } from '@shared/theme/ThemeContext';
import CalendarDayView from '../components/calendar/CalendarDayView';
import { CalendarEventDisplay } from '../utils/calendarUtils';
import CalendarAgendaView from '../components/calendar/CalendarAgendaView';
import { toLocalDateString } from '../utils/dateUtils';
import ExportMenu from '../components/ExportMenu';
import ViewToggle from '../components/ViewToggle';

// Types - use shared types where possible
interface CalendarSummary {
  total_events: number;
  total_bills: number;
  tracked_bills: number;
  senate_chamber: number;
  senate_committee: number;
  house_chamber: number;
  house_committee: number;
  joint: number;
}

interface CalendarMetadata {
  title: string;
  subtitle?: string;
  organization?: string;
  workspace?: string;
  description?: string;
  logoUrl?: string;
  logoSettings?: {
    showOrganizationLogo?: boolean;
    showWorkspaceLogo?: boolean;
    logoPosition?: string;
  };
  logoUrls?: {
    organization?: string;
    workspace?: string;
  };
}

interface PublicCalendarData {
  scope: 'all' | 'org' | 'ws';
  scopeId?: string;
  date?: string;
  startDate?: string;
  endDate?: string;
  events: CalendarEventDisplay[];
  summary: CalendarSummary;
  metadata: CalendarMetadata;
}

// API client without auth
const publicApi = axios.create({
  baseURL: '/api',
});

// Fetch public calendar data
const fetchPublicCalendar = async (scope: string, scopeId: string | null, date: string): Promise<PublicCalendarData> => {
  let url = '';
  
  if (scope === 'all') {
    url = `/public/calendar/all/${date}`;
  } else if (scope === 'org' && scopeId) {
    url = `/public/calendar/org/${scopeId}/${date}`;
  } else if (scope === 'ws' && scopeId) {
    url = `/public/calendar/ws/${scopeId}/${date}`;
  } else {
    throw new Error('Invalid calendar scope');
  }

  const response = await publicApi.get(url);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to load calendar');
  }
  return response.data.data;
};

const fetchPublicCalendarRange = async (
  scope: string,
  scopeId: string | null,
  rangeType: 'week' | 'month',
  startDate: string
): Promise<PublicCalendarData> => {
  let url = '';

  if (scope === 'all') {
    url = `/public/calendar/all/${rangeType}/${startDate}`;
  } else if (scope === 'org' && scopeId) {
    url = `/public/calendar/org/${scopeId}/${rangeType}/${startDate}`;
  } else if (scope === 'ws' && scopeId) {
    url = `/public/calendar/ws/${scopeId}/${rangeType}/${startDate}`;
  } else {
    throw new Error('Invalid calendar scope');
  }

  const response = await publicApi.get(url);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to load calendar');
  }
  return response.data.data;
};

// Export calendar
const exportCalendar = async (scope: string, scopeId: string | null, date: string, format: string): Promise<void> => {
  let url = '';
  
  if (scope === 'all') {
    url = `/public/calendar/all/${date}/export/${format}`;
  } else if (scope === 'org' && scopeId) {
    url = `/public/calendar/org/${scopeId}/${date}/export/${format}`;
  } else if (scope === 'ws' && scopeId) {
    url = `/public/calendar/ws/${scopeId}/${date}/export/${format}`;
  } else {
    throw new Error('Invalid calendar scope');
  }

  const response = await publicApi.get(url, { responseType: 'blob' });
  
  const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = blobUrl;
  
  // Get filename from headers or generate one
  const disposition = response.headers['content-disposition'];
  let filename = `calendar-${date}.${format === 'excel' ? 'xlsx' : format === 'word' ? 'docx' : format}`;
  
  if (disposition && disposition.includes('filename=')) {
    const filenameMatch = disposition.match(/filename="([^"]+)"/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }
  
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

const exportCalendarRange = async (
  scope: string,
  scopeId: string | null,
  rangeType: 'week' | 'month',
  startDate: string,
  format: string
): Promise<void> => {
  let url = '';

  if (scope === 'all') {
    url = `/public/calendar/all/${rangeType}/${startDate}/export/${format}`;
  } else if (scope === 'org' && scopeId) {
    url = `/public/calendar/org/${scopeId}/${rangeType}/${startDate}/export/${format}`;
  } else if (scope === 'ws' && scopeId) {
    url = `/public/calendar/ws/${scopeId}/${rangeType}/${startDate}/export/${format}`;
  } else {
    throw new Error('Invalid calendar scope');
  }

  const response = await publicApi.get(url, { responseType: 'blob' });

  const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = blobUrl;

  // Get filename from headers or generate one
  const disposition = response.headers['content-disposition'];
  let filename = `calendar-${startDate}.${format === 'excel' ? 'xlsx' : format === 'word' ? 'docx' : format}`;

  if (disposition && disposition.includes('filename=')) {
    const filenameMatch = disposition.match(/filename="([^"]+)"/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

// Components: Using shared CalendarEventCard and CalendarEventTable from components/shared/

// Main Component
const PublicCalendarPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Parse path parts to determine scope and IDs
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  
  // Determine scope and IDs from URL path
  // /public/calendar/all/:date
  // /public/calendar/org/:orgId/:date
  // /public/calendar/ws/:workspaceId/:date
  // /public/calendar/all/week/:startDate
  // /public/calendar/org/:orgId/week/:startDate
  // /public/calendar/ws/:workspaceId/week/:startDate
  // /public/calendar/all/month/:startDate
  // /public/calendar/org/:orgId/month/:startDate
  // /public/calendar/ws/:workspaceId/month/:startDate
  let scope: 'all' | 'org' | 'ws' = 'all';
  let scopeId: string | null = null;
  let mode: 'day' | 'week' | 'month' = 'day';
  let date: string = '';
  let startDate: string = '';

  if (pathParts[2] === 'all') {
    scope = 'all';
    if (pathParts[3] === 'week') {
      mode = 'week';
      startDate = pathParts[4] || '';
    } else if (pathParts[3] === 'month') {
      mode = 'month';
      startDate = pathParts[4] || '';
    } else {
      mode = 'day';
      date = pathParts[3] || '';
    }
  } else if (pathParts[2] === 'org') {
    scope = 'org';
    scopeId = pathParts[3] || null;
    if (pathParts[4] === 'week') {
      mode = 'week';
      startDate = pathParts[5] || '';
    } else if (pathParts[4] === 'month') {
      mode = 'month';
      startDate = pathParts[5] || '';
    } else {
      mode = 'day';
      date = pathParts[4] || '';
    }
  } else if (pathParts[2] === 'ws') {
    scope = 'ws';
    scopeId = pathParts[3] || null;
    if (pathParts[4] === 'week') {
      mode = 'week';
      startDate = pathParts[5] || '';
    } else if (pathParts[4] === 'month') {
      mode = 'month';
      startDate = pathParts[5] || '';
    } else {
      mode = 'day';
      date = pathParts[4] || '';
    }
  }

  const [viewMode, setViewMode] = useState<'card' | 'table'>(mode === 'day' ? 'card' : 'table');

  // Fetch calendar data
  const { data: calendarData, isLoading, error } = useQuery(
    ['public-calendar', scope, scopeId, mode, date, startDate],
    () => {
      if (mode === 'day') {
        return fetchPublicCalendar(scope, scopeId, date);
      }
      return fetchPublicCalendarRange(scope, scopeId, mode, startDate);
    },
    {
      enabled: mode === 'day' ? !!date : !!startDate,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  // Handle click outside export menu
  // Navigation helpers
  const navigateToDate = (newDate: string) => {
    let path = '';
    if (scope === 'all') {
      path = `/public/calendar/all/${newDate}`;
    } else if (scope === 'org' && scopeId) {
      path = `/public/calendar/org/${scopeId}/${newDate}`;
    } else if (scope === 'ws' && scopeId) {
      path = `/public/calendar/ws/${scopeId}/${newDate}`;
    }
    navigate(path);
  };

  const navigateToWeek = (newStartDate: string) => {
    let path = '';
    if (scope === 'all') {
      path = `/public/calendar/all/week/${newStartDate}`;
    } else if (scope === 'org' && scopeId) {
      path = `/public/calendar/org/${scopeId}/week/${newStartDate}`;
    } else if (scope === 'ws' && scopeId) {
      path = `/public/calendar/ws/${scopeId}/week/${newStartDate}`;
    }
    navigate(path);
  };

  const navigateToMonth = (newStartDate: string) => {
    let path = '';
    if (scope === 'all') {
      path = `/public/calendar/all/month/${newStartDate}`;
    } else if (scope === 'org' && scopeId) {
      path = `/public/calendar/org/${scopeId}/month/${newStartDate}`;
    } else if (scope === 'ws' && scopeId) {
      path = `/public/calendar/ws/${scopeId}/month/${newStartDate}`;
    }
    navigate(path);
  };

  const goToToday = () => {
    const today = toLocalDateString(new Date());
    if (mode === 'day') {
      navigateToDate(today);
      return;
    }
    if (mode === 'week') {
      navigateToWeek(today);
      return;
    }
    navigateToMonth(today);
  };

  const isToday = mode === 'day' && date === toLocalDateString(new Date());

  // Format display date/range
  const displayDate = (() => {
    if (mode === 'day') {
      return date ? new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }) : '';
    }
    const start = calendarData?.startDate || startDate;
    const end = calendarData?.endDate || '';
    if (!start) return '';
    const startObj = new Date(start + 'T00:00:00');
    const endObj = end ? new Date(end + 'T00:00:00') : null;
    if (!endObj || isNaN(endObj.getTime()) || isNaN(startObj.getTime())) {
      return start;
    }
    const sameMonth = startObj.getFullYear() === endObj.getFullYear() && startObj.getMonth() === endObj.getMonth();
    if (sameMonth) {
      return `${startObj.toLocaleDateString('en-US', { month: 'long' })} ${startObj.getDate()} - ${endObj.getDate()}, ${endObj.getFullYear()}`;
    }
    return `${startObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${endObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  })();

  // Previous/Next navigation labels
  const stepDays = mode === 'month' ? 30 : mode === 'week' ? 7 : 1;

  const prevLabel = (() => {
    const anchor = mode === 'day' ? date : (calendarData?.startDate || startDate);
    const d = new Date((anchor || toLocalDateString(new Date())) + 'T00:00:00');
    d.setDate(d.getDate() - stepDays);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  })();

  const nextLabel = (() => {
    const anchor = mode === 'day' ? date : (calendarData?.startDate || startDate);
    const d = new Date((anchor || toLocalDateString(new Date())) + 'T00:00:00');
    d.setDate(d.getDate() + stepDays);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  })();

  // Handle export
  const handleExport = async (format: 'csv' | 'excel' | 'pdf' | 'word') => {
    try {
      if (mode === 'day') {
        await exportCalendar(scope, scopeId, date, format);
      } else {
        const effectiveStartDate = calendarData?.startDate || startDate;
        await exportCalendarRange(scope, scopeId, mode, effectiveStartDate, format);
      }
      toast.success('Calendar exported successfully');
    } catch (err) {
      toast.error('Failed to export calendar');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <ThemeProvider>
        <div className="min-h-screen bg-background dark:bg-background-dark-primary flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
              Loading Calendar...
            </h2>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  // Error state
  if (error || !calendarData) {
    return (
      <ThemeProvider>
        <div className="min-h-screen bg-background dark:bg-background-dark-primary flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-4">
              Calendar Not Found
            </h2>
            <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
              {(error as Error)?.message || 'This calendar is not available.'}
            </p>
            <button
              onClick={goToToday}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              View Today&apos;s Calendar
            </button>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  const { events, summary, metadata } = calendarData;

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-background dark:bg-background-dark-primary">
        {/* Header */}
        <div className="bg-white dark:bg-background-dark-secondary border-b border-border-light dark:border-border-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Logo and Title */}
            <div className="flex items-start gap-4 mb-6">
              {(metadata.logoUrl || metadata.logoUrls?.organization || metadata.logoUrls?.workspace) && (
                <img
                  src={metadata.logoUrl || metadata.logoUrls?.organization || metadata.logoUrls?.workspace}
                  alt="Organization Logo"
                  className="h-16 w-auto object-contain"
                />
              )}
              <div>
                <h1 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary">
                  {metadata.title || 'Legislative Calendar'}
                </h1>
                {metadata.subtitle && (
                  <p className="text-text-secondary dark:text-text-dark-secondary mt-1">
                    {metadata.subtitle}
                  </p>
                )}
                {metadata.description && (
                  <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary mt-1">
                    {metadata.description}
                  </p>
                )}
              </div>
            </div>

            {/* Date Navigation */}
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const anchor = mode === 'day' ? date : (calendarData.startDate || startDate);
                    const d = new Date(anchor + 'T00:00:00');
                    d.setDate(d.getDate() - stepDays);
                    const newDate = toLocalDateString(d);
                    if (mode === 'day') return navigateToDate(newDate);
                    if (mode === 'week') return navigateToWeek(newDate);
                    return navigateToMonth(newDate);
                  }}
                  className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-text-secondary hover:text-text-primary dark:text-text-dark-secondary dark:hover:text-text-dark-primary bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg hover:bg-neutral-200 dark:hover:bg-background-dark-secondary transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {prevLabel}
                </button>
                
                <button
                  onClick={goToToday}
                  disabled={isToday}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isToday
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 cursor-default'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  Today
                </button>
                
                <button
                  onClick={() => {
                    const anchor = mode === 'day' ? date : (calendarData.startDate || startDate);
                    const d = new Date(anchor + 'T00:00:00');
                    d.setDate(d.getDate() + stepDays);
                    const newDate = toLocalDateString(d);
                    if (mode === 'day') return navigateToDate(newDate);
                    if (mode === 'week') return navigateToWeek(newDate);
                    return navigateToMonth(newDate);
                  }}
                  className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-text-secondary hover:text-text-primary dark:text-text-dark-secondary dark:hover:text-text-dark-primary bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg hover:bg-neutral-200 dark:hover:bg-background-dark-secondary transition-colors"
                >
                  {nextLabel}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              <div className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary" />
                <span className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                  {displayDate}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Stats Bar */}
        <div className="bg-background-tertiary dark:bg-background-dark-tertiary border-b border-border-light dark:border-border-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="flex items-center gap-1 text-text-secondary dark:text-text-dark-secondary">
                <FileText className="w-4 h-4" />
                <span className="font-medium">{summary.tracked_bills}</span> Bills
              </span>
              <span className="text-border-light dark:text-border-dark">|</span>
              <span className="flex items-center gap-1">
                <span className="text-blue-600 dark:text-blue-400">{summary.senate_chamber}</span> Senate Chamber
              </span>
              <span className="flex items-center gap-1">
                <span className="text-blue-600 dark:text-blue-400">{summary.senate_committee}</span> Senate Committee
              </span>
              <span className="text-border-light dark:text-border-dark">|</span>
              <span className="flex items-center gap-1">
                <span className="text-red-600 dark:text-red-400">{summary.house_chamber}</span> House Chamber
              </span>
              <span className="flex items-center gap-1">
                <span className="text-red-600 dark:text-red-400">{summary.house_committee}</span> House Committee
              </span>
              <span className="text-border-light dark:text-border-dark">|</span>
              <span className="flex items-center gap-1 font-medium text-text-primary dark:text-text-dark-primary">
                <CalendarIcon className="w-4 h-4" />
                {summary.total_events} Total
              </span>
            </div>
          </div>
        </div>

        {/* View Toggle and Export */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* View Toggle - hidden on mobile (cards only on mobile) */}
            <div className="hidden lg:flex items-center space-x-2">
              <ViewToggle
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { id: 'card', label: 'Card View includes all events', icon: LayoutGrid },
                  { id: 'table', label: 'List View includes only bill related events', icon: List },
                ]}
              />
            </div>

            {/* Export Dropdown */}
            <ExportMenu
              variant="primary"
              formats={
                viewMode === 'table'
                  ? ['csv', 'excel', { format: 'pdf', note: 'Landscape' }]
                  : [{ format: 'word', note: 'Portrait' }, { format: 'pdf', note: 'Portrait' }]
              }
              onExport={handleExport}
            />
          </div>
        </div>

        {/* Events Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          {events.length === 0 ? (
            <div className="card text-center py-12">
              <CalendarIcon className="w-16 h-16 text-text-tertiary dark:text-text-dark-tertiary mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
                No Events Scheduled
              </h3>
              <p className="text-text-secondary dark:text-text-dark-secondary">
                {scope === 'all' 
                  ? 'There are no legislative events scheduled for this day.'
                  : 'No events with tracked bills are scheduled for this day.'}
              </p>
            </div>
          ) : (
            <>
              {mode !== 'day' && viewMode === 'card' ? (
                <CalendarAgendaView
                  events={events}
                  isPublic={true}
                  scope={calendarData?.scope}
                />
              ) : (
                <CalendarDayView
                  events={events}
                  viewMode={viewMode}
                  isPublic={true}
                  scope={calendarData?.scope}
                  showDateColumn={mode !== 'day'}
                />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="bg-background-tertiary dark:bg-background-dark-tertiary border-t border-border-light dark:border-border-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center space-y-4">
            {/* Login / signup prompt */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link
                to={`/login?returnUrl=${encodeURIComponent(mode === 'day' && date ? `/calendar/${date}` : (startDate ? `/calendar/${startDate}` : '/calendar'))}`}
                className="px-4 py-2 border border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 text-sm font-medium rounded-lg transition-colors"
              >
                Login
              </Link>
              <span className="text-text-tertiary dark:text-text-dark-tertiary text-sm">or</span>
              <Link
                to="/citizen/signup"
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Sign up for more details
              </Link>
            </div>
            <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
              Powered by{' '}
              <a 
                href="https://capitolcommons.co" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Capitol Commons
              </a>
            </p>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default PublicCalendarPage;

